import type { FacilityContractVersion } from '../../types/contractWorkspace';
import type {
  ActivationReadiness,
  ReadinessAction,
  ReadinessItem,
  ReadinessKind,
  ReadinessSectionKey,
  ReadinessStatus,
} from '../../types/activationReadiness';
import { businessLabel, readJsonValue, termGroup } from '../../presentation/contractPresentation';

/** A citation as the reviewer sees it. Missing on a gating item is a problem, never a blank. */
export interface Citation {
  page: string | null;
  section: string | null;
  clause: string | null;
  excerpt: string | null;
}

/** One reviewable thing on the page: a term, rule, covenant, account, calendar or condition. */
export interface ReviewRow {
  uid: string;
  kind: ReadinessKind;
  section: ReadinessSectionKey;
  id: number | null;
  key: string;
  label: string;
  group: string;
  value: string;
  detail: string | null;
  extractedValue: string | null;
  corrected: boolean;
  citation: Citation | null;
  entered: { by: string; at: string | null; source: string } | null;
  confidence: number | null;
  status: ReadinessStatus;
  problem: string | null;
  message: string | null;
  owner: 'MAKER' | 'CHECKER' | null;
  actions: ReadinessAction[];
  valueType: string | null;
  required: boolean;
  material: boolean;
  /** Set when the maker's change summary names this item as changed against the predecessor (F-S2-CTRL-002). */
  changeNote?: string | null;
}

export const SECTION_LABEL: Record<ReadinessSectionKey, string> = {
  fund: 'Fund',
  terms: 'Contract terms',
  eligibility: 'Eligibility rules',
  covenants: 'Covenants',
  accounts: 'Accounts',
  calendar: 'Payment calendar',
  schedules: 'Contract schedules',
  conditions: 'Conditions precedent',
};

export const KIND_LABEL: Record<ReadinessKind, string> = {
  FUND: 'Fund',
  TERM: 'Term',
  RULE: 'Eligibility rule',
  COVENANT: 'Covenant',
  ACCOUNT: 'Account',
  CALENDAR: 'Payment calendar',
  SCHEDULE: 'Schedule',
  CONDITION: 'Condition precedent',
};

export const PROBLEM_LABEL: Record<string, string> = {
  NO_SOURCE: 'Needs a source',
  NO_VALUE: 'Needs a value',
  UNCONFIRMED: 'Needs confirmation',
  REJECTED: 'Rejected',
  INCOMPLETE_DEFINITION: 'Incomplete definition',
  UNSUPPORTED_FORMULA: 'Not computable yet',
  NO_ACCOUNT_NUMBER: 'Needs account number',
  INCOMPLETE_ACCOUNT: 'Incomplete account',
  INCOMPLETE_CALENDAR: 'Payment calendar needed',
  INCOMPLETE_SCHEDULE: 'Incomplete schedule',
  OPEN_CONDITION: 'Open condition',
  NO_FUND: 'Needs a fund',
};

const text = (value: unknown): string | null => {
  if (value == null) return null;
  const trimmed = String(value).trim();
  return trimmed ? trimmed : null;
};

const citationOf = (row: Record<string, unknown>, excerpt?: unknown): Citation | null => {
  const page = text(row.source_page ?? row.sourcePage);
  const section = text(row.source_section ?? row.sourceSection);
  const clause = text(row.source_clause ?? row.sourceClause);
  if (!page && !section && !clause) return null;
  return { page, section, clause, excerpt: text(excerpt) };
};

const MONEY = new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 });

export const formatMoney = (value: unknown, currency?: string | null): string => {
  const parsed = readJsonValue(value);
  const numeric = typeof parsed === 'number' ? parsed : Number(parsed);
  if (parsed == null || parsed === '' || Number.isNaN(numeric)) return 'Not stated';
  return `${currency ? `${currency} ` : ''}${MONEY.format(numeric)}`;
};

export const formatDate = (value: unknown): string => {
  const raw = text(readJsonValue(value));
  if (!raw) return 'Not stated';
  const date = new Date(`${raw.slice(0, 10)}T12:00:00`);
  return Number.isNaN(date.getTime())
    ? raw
    : new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' }).format(date);
};

/** Audit timestamps in the viewer's zone, never raw UTC strings (FR-0.12). */
export const formatStamp = (value: unknown): string => {
  const raw = text(value);
  if (!raw) return '';
  const date = new Date(raw.includes('T') || raw.includes(' ') ? raw.replace(' ', 'T') : `${raw}T12:00:00`);
  return Number.isNaN(date.getTime())
    ? raw
    : new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }).format(date);
};

const TERM_UNITS: Record<string, (value: unknown, currency?: string | null) => string> = {
  totalCommitment: formatMoney,
  drawnAmount: formatMoney,
  reserveAmount: formatMoney,
  advanceRatePct: value => `${readJsonValue(value)}%`,
  rateFloorPct: value => `${readJsonValue(value)}%`,
  allInRatePct: value => `${readJsonValue(value)}%`,
  spreadBps: value => `${readJsonValue(value)} bps`,
  unusedFeeBps: value => `${readJsonValue(value)} bps`,
  tickingFeeBps: value => `${readJsonValue(value)} bps`,
  reborrowAllowed: value => (String(readJsonValue(value)).toLowerCase() === 'true' ? 'Yes' : 'No'),
  amortizationStartDate: formatDate,
  closingDate: formatDate,
  effectiveDate: formatDate,
  maturityDate: formatDate,
  commitmentTerminationDate: formatDate,
  nextBorrowingBaseDate: formatDate,
  dayCount: value => String(readJsonValue(value)).replace('ACT_', 'Actual/').replace('_', '/'),
};

export const formatTermValue = (key: string, value: unknown, presence?: string | null, currency?: string | null) => {
  const normalized = String(presence || '').toUpperCase();
  if (normalized === 'NOT_STATED') return 'Not stated in the agreement';
  const parsed = readJsonValue(value);
  if (parsed == null || parsed === '') return normalized === 'UNKNOWN' ? 'Not found' : 'Not stated';
  const format = TERM_UNITS[key];
  if (format) return format(parsed, currency);
  if (typeof parsed === 'number') return MONEY.format(parsed);
  return String(parsed).replace(/_/g, ' ');
};

const FORMULA_LABEL: Record<string, string> = {
  DELINQUENCY_PCT: '30+ day delinquent balance ÷ pool balance',
  NET_LOSS_RATE_PCT: 'Net charge-offs ÷ pool balance',
  EXCESS_AVAILABILITY: 'Lesser of commitment and borrowing base, less drawn and pending draws',
  ADVANCE_RATE_PCT: 'Drawn ÷ eligible collateral',
  OVERCOLLATERALIZATION_PCT: 'Eligible collateral ÷ drawn',
  EXCESS_SPREAD_PCT: 'Weighted asset yield less funding rate',
  LARGEST_STATE_CONCENTRATION_PCT: 'Largest state ÷ pool balance',
  MATCHED_CASH_COVERAGE_PCT: 'Matched bank cash ÷ drawn',
};

const covenantLevel = (covenant: Record<string, unknown>, currency?: string | null) => {
  const formula = String(covenant.formula_expression || '');
  const amount = covenant.threshold_value;
  const level = formula === 'EXCESS_AVAILABILITY'
    ? formatMoney(amount, currency)
    : `${MONEY.format(Number(amount))}%`;
  const type = String(covenant.threshold_type || '').toUpperCase();
  return `${type === 'MIN' ? 'At least' : type === 'MAX' ? 'At most' : 'Level'} ${level}`;
};

const payloadExcerpt = (row: Record<string, unknown>): unknown => {
  const payload = readJsonValue(row.extracted_payload_json);
  return payload && typeof payload === 'object' ? (payload as Record<string, unknown>).source_excerpt : null;
};

const ACCOUNT_TYPE_LABEL: Record<string, string> = {
  COLLECTION: 'Collection account',
  LOCKBOX: 'Lockbox account',
  RESERVE: 'Reserve account',
  PAYMENT: 'Payment account',
  DISTRIBUTION: 'Distribution account',
  OTHER: 'Account',
};

/** Joins the version record (values, citations) with the server verdict (status, fix). */
export const buildReviewRows = (
  version: FacilityContractVersion,
  readiness: ActivationReadiness,
): ReviewRow[] => {
  // The UI lane has no document to read from, so every value is hand-entered.
  const manualLane = readiness.document?.lane === 'MANUAL';
  const authorityReference = readiness.document?.authorityReference || 'the agreement entered';
  const handEntered = (by: string | null | undefined, at: string | null | undefined, source: string) =>
    manualLane && by ? { by, at: at || null, source } : null;
  const currency = String(readJsonValue(
    version.terms.find(term => term.term_key === 'currency')?.verified_value_json
      ?? version.terms.find(term => term.term_key === 'currency')?.extracted_value_json,
  ) || '') || null;
  const verdict = new Map<string, ReadinessItem>(
    readiness.items.map(item => [`${item.kind}:${item.id ?? item.key}`, item]),
  );
  const base = (kind: ReadinessKind, id: number | null, key: string) => {
    const item = verdict.get(`${kind}:${id ?? key}`);
    return {
      uid: `${kind}:${id ?? key}`,
      kind,
      id,
      key,
      section: item?.section || ('terms' as ReadinessSectionKey),
      status: item?.status || ('INFO' as ReadinessStatus),
      problem: item?.problem || null,
      message: item?.message || null,
      owner: item?.owner || null,
      actions: item?.actions || [],
    };
  };
  const rows: ReviewRow[] = [];

  // The fund is a booking decision taken in review, so it carries its chooser, not a clause.
  const fund = readiness.fund;
  if (fund) {
    rows.push({
      ...base('FUND', fund.portfolioId, 'portfolioId'),
      section: 'fund',
      label: 'Fund',
      group: 'Fund',
      value: fund.holders && fund.holders.length > 1
        ? fund.holders.map(holder => `${holder.portfolioCode || holder.portfolioName || holder.portfolioId} ${Number(Number(holder.sharePct).toFixed(6))}%`).join(' · ')
        : fund.portfolioName
          ? `${fund.portfolioName}${fund.portfolioCode ? ` (${fund.portfolioCode})` : ''}`
          : 'Not chosen',
      detail: fund.inheritedFromFacility
        ? 'Set when the facility was created'
        : fund.holders && fund.holders.length > 1
          ? 'The funds that hold this facility, with their shares of the commitment'
          : 'Where this facility and its trades will book',
      extractedValue: null,
      corrected: false,
      citation: null,
      entered: fund.chosenBy
        ? { by: fund.chosenBy, at: fund.chosenAt || null, source: 'chosen in contract review' }
        : null,
      confidence: null,
      valueType: null,
      required: true,
      material: true,
    });
  }

  for (const term of version.terms) {
    if (term.term_key === 'portfolioId') continue;
    const extracted = formatTermValue(term.term_key, term.extracted_value_json, null, currency);
    const current = readJsonValue(term.verified_value_json) != null
      ? formatTermValue(term.term_key, term.verified_value_json, term.value_presence, currency)
      : formatTermValue(term.term_key, term.extracted_value_json, term.value_presence, currency);
    const corrected = readJsonValue(term.verified_value_json) != null
      && readJsonValue(term.extracted_value_json) != null
      && JSON.stringify(readJsonValue(term.verified_value_json)) !== JSON.stringify(readJsonValue(term.extracted_value_json));
    rows.push({
      ...base('TERM', term.id, term.term_key),
      section: 'terms',
      label: businessLabel(term.term_key),
      group: termGroup(term.term_key),
      value: current,
      detail: null,
      extractedValue: corrected ? extracted : null,
      corrected,
      citation: citationOf(term as unknown as Record<string, unknown>),
      // Nothing was extracted in the UI lane: a present value is a person's
      // assertion under the named agreement, and a checker has to see that it
      // is an assertion rather than an independently confirmed reading
      // (F-UIL-008).
      entered: handEntered(term.verified_by, term.verified_at, authorityReference),
      confidence: term.extraction_confidence,
      valueType: term.value_type,
      required: Boolean(term.required_for_activation),
      material: Boolean(term.economically_material),
    });
  }

  // The maker's change summary names what this version alters; carry it onto
  // the rows so the checker sees "changed" beside the item, not only in the panel.
  const summary = version.predecessor_version_id
    ? (readJsonValue(version.change_summary_json) as Record<string, unknown> | null) || {}
    : {};
  const ruleChanges = new Map<string, string>();
  const rawCorrections = version.ruleClassificationCorrections
    ?? (Array.isArray(summary.ruleClassificationCorrections) ? summary.ruleClassificationCorrections as unknown[] : []);
  for (const entry of rawCorrections) {
    const item = (entry || {}) as Record<string, unknown>;
    if (item.ruleName) ruleChanges.set(String(item.ruleName), `Reclassified from ${item.fromType} to ${item.toType}`);
  }
  const changedTermKeys = new Set(Array.isArray(summary.changedTerms) ? summary.changedTerms.map(String) : []);
  for (const row of rows) if (row.kind === 'TERM' && changedTermKeys.has(row.key)) row.changeNote = 'Changed in this version';

  for (const rule of version.eligibilityRules) {
    const raw = rule as unknown as Record<string, unknown>;
    rows.push({
      ...base('RULE', rule.id, rule.rule_name),
      section: 'eligibility',
      label: rule.rule_name,
      group: 'Eligibility rules',
      value: rule.threshold_text || `${rule.operator || ''} ${rule.threshold_value ?? ''}`.trim(),
      detail: rule.field_name ? `Tested on the tape field “${rule.field_name.replace(/_/g, ' ')}”`
        : (raw.rule_type === 'LEGAL' ? 'Legal criterion: part of the contract, not tested against a tape' : 'No tape field'),
      extractedValue: null,
      corrected: false,
      citation: citationOf(raw, payloadExcerpt(raw)),
      entered: null,
      confidence: null,
      valueType: null,
      required: true,
      material: true,
      changeNote: ruleChanges.get(rule.rule_name) || null,
    });
  }

  for (const covenant of version.covenants) {
    const raw = covenant as unknown as Record<string, unknown>;
    rows.push({
      ...base('COVENANT', covenant.id, covenant.covenant_name),
      section: 'covenants',
      label: covenant.covenant_name,
      group: 'Covenants',
      value: covenantLevel(raw, currency),
      detail: covenant.formula_expression
        ? FORMULA_LABEL[covenant.formula_expression] || businessLabel(covenant.formula_expression)
        : 'No computable formula',
      extractedValue: null,
      corrected: false,
      citation: citationOf(raw, payloadExcerpt(raw)),
      entered: null,
      confidence: null,
      valueType: null,
      required: true,
      material: true,
    });
  }

  const accounts = version.contractConfiguration?.accounts || [];
  for (const account of accounts) {
    const completion = account.completion || null;
    rows.push({
      ...base('ACCOUNT', null, account.accountKey),
      section: 'accounts',
      label: account.accountName || ACCOUNT_TYPE_LABEL[account.accountType] || businessLabel(account.accountType),
      group: 'Accounts',
      value: account.maskedAccountNumber || 'Number not in the agreement',
      detail: [account.bankName, ACCOUNT_TYPE_LABEL[account.accountType]].filter(Boolean).join(' · ') || null,
      extractedValue: null,
      corrected: false,
      citation: citationOf(account as unknown as Record<string, unknown>),
      entered: completion
        ? { by: completion.completedBy, at: completion.completedAt || null, source: completion.sourceReference }
        : null,
      confidence: null,
      valueType: null,
      required: true,
      material: true,
    });
  }

  const calendar = version.contractConfiguration?.paymentCalendar;
  if (calendar && 'determinationDay' in calendar) {
    rows.push({
      ...base('CALENDAR', null, 'paymentCalendar'),
      section: 'calendar',
      label: 'Payment calendar',
      group: 'Payment calendar',
      value: `${businessLabel(calendar.collectionPeriod)} periods · determination day ${calendar.determinationDay} · payment day ${calendar.paymentDay}`,
      detail: `${businessLabel(calendar.businessDayConvention)} business day · ${calendar.holidayCalendar === 'US_FEDERAL' ? 'US federal holidays' : businessLabel(calendar.holidayCalendar)}`,
      extractedValue: null,
      corrected: false,
      citation: citationOf(calendar as unknown as Record<string, unknown>),
      entered: null,
      confidence: null,
      valueType: null,
      required: true,
      material: true,
    });
  }

  for (const cp of version.conditionsPrecedent) {
    rows.push({
      ...base('CONDITION', cp.id, cp.cp_key),
      section: 'conditions',
      label: cp.description || businessLabel(cp.cp_key),
      group: 'Conditions precedent',
      value: businessLabel(cp.status),
      detail: cp.required_for === 'ACTIVATION' ? 'Required before activation' : `Required for ${businessLabel(cp.required_for).toLowerCase()}`,
      extractedValue: null,
      corrected: false,
      citation: cp.source_clause ? { page: null, section: null, clause: cp.source_clause, excerpt: null } : null,
      entered: null,
      confidence: null,
      valueType: null,
      required: cp.required_for === 'ACTIVATION',
      material: true,
    });
  }

  for (const item of readiness.items.filter(entry => entry.kind === 'SCHEDULE')) {
    rows.push({
      ...base('SCHEDULE', null, item.key),
      section: 'schedules',
      label: businessLabel(item.key),
      group: 'Contract schedules',
      value: item.message || '',
      detail: null,
      extractedValue: null,
      corrected: false,
      citation: null,
      entered: null,
      confidence: null,
      valueType: null,
      required: false,
      material: false,
    });
  }
  return rows;
};

/** Order open items so the reviewer works one table at a time, top of the agreement first. */
export const SECTION_ORDER: ReadinessSectionKey[] = [
  'fund', 'terms', 'eligibility', 'covenants', 'accounts', 'calendar', 'schedules', 'conditions',
];

export const openRows = (rows: ReviewRow[]) => rows
  .filter(row => row.status === 'OPEN')
  .sort((left, right) => SECTION_ORDER.indexOf(left.section) - SECTION_ORDER.indexOf(right.section)
    || left.group.localeCompare(right.group));

/** The citation most siblings of the same kind already carry, offered as a one-click suggestion. */
export const siblingCitation = (rows: ReviewRow[], row: ReviewRow): Citation | null => {
  const counts = new Map<string, { citation: Citation; count: number }>();
  for (const candidate of rows) {
    if (candidate.uid === row.uid || candidate.kind !== row.kind || !candidate.citation) continue;
    if (row.kind === 'TERM' && candidate.group !== row.group) continue;
    const key = `${candidate.citation.page || ''}|${candidate.citation.section || ''}`;
    const entry = counts.get(key) || { citation: candidate.citation, count: 0 };
    entry.count += 1;
    counts.set(key, entry);
  }
  let best: { citation: Citation; count: number } | null = null;
  counts.forEach(entry => { if (!best || entry.count > best.count) best = entry; });
  return best ? (best as { citation: Citation }).citation : null;
};

export const citationText = (citation: Citation | null): string => {
  if (!citation) return '';
  // Clause before section: in the UI lane the section holds the agreement
  // reference, so showing it first made every row read identically and hid the
  // per-term citation the maker gave (F-UIL-007).
  return [citation.page ? `p. ${citation.page}` : null, citation.clause || citation.section]
    .filter(Boolean).join(' · ');
};

/** Converts a typed value to the term's JSON type before it reaches the server. */
export const typedTermValue = (valueType: string | null, raw: string): unknown => {
  const value = raw.trim();
  const type = String(valueType || '').toUpperCase();
  if (type === 'NUMBER') return Number(value.replace(/[$,%\s]/g, ''));
  if (type === 'INTEGER') return Math.round(Number(value.replace(/[,\s]|bps/gi, '')));
  return value;
};
